import { useCallback, useEffect, useRef } from "react";

export const useSubscriptionRetry = () => {
  const timeoutIdRef = useRef(null);
  const cancelledRef = useRef(false);
  const successCalledRef = useRef(false);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      successCalledRef.current = false;
    };
  }, []);

  const start = useCallback(
    ({ fetchSubscription, onSuccess, onTimeout, expectedPlanId }) => {
      const CHECK_INTERVAL_MS = 10 * 1000;
      const MAX_DURATION_MS = 10 * 30 * 1000;
      const MAX_RETRIES = Math.floor(MAX_DURATION_MS / CHECK_INTERVAL_MS);

      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }

      cancelledRef.current = false;
      successCalledRef.current = false;

      const scheduleNextRetry = (retryIndex) => {
        if (cancelledRef.current || successCalledRef.current) {
          return;
        }
        if (retryIndex >= MAX_RETRIES) {
          cancelledRef.current = true;
          onTimeout();
          return;
        }
        timeoutIdRef.current = window.setTimeout(async () => {
          if (cancelledRef.current || successCalledRef.current) {
            return;
          }
          try {
            console.log(`Retrying subscription fetch (attempt ${retryIndex + 1})..., calling fetchSubscription from hook`);
            let res = await fetchSubscription();
            const data = res[0];
            if (data) {
              successCalledRef.current = true;
              cancelledRef.current = true;
              if (timeoutIdRef.current !== null) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
              }
              console.log("Subscription is ready, calling onSuccess from hook");
              onSuccess(data, res);
              return;
            }
          } catch {
            // Ignore errors and continue retrying
          }
          console.log(`Subscription not ready yet (attempt ${retryIndex + 1}), scheduling next retry...`);
          scheduleNextRetry(retryIndex + 1);
        }, CHECK_INTERVAL_MS);
      };

      scheduleNextRetry(0);
    },
    []
  );

  return { start };
};

export default useSubscriptionRetry;
